import React, { useState } from 'react';
import { ImportSourceType } from '../../../services/importService';

interface Props {
  source: number;
  connectionData: { headers?: string[]; kind?: string; rows?: unknown[][] } | null;
  onContinue: (mapping: Record<string, string>) => void;
}

const DAGRUN_FIELDS = [
  'dagrun_pm_name',
  'dagrun_pm_status',
  'dagrun_pm_priority',
  'dagrun_pm_assigneename',
  'dagrun_pm_duedate',
  'dagrun_pm_startdate',
  'dagrun_pm_percentcomplete',
  'dagrun_pm_estimatedhours',
  'dagrun_pm_description',
  '(skip)',
];

// Heuristics: match on column title (case-insensitive substring). Ordered
// first-match-wins so more specific checks come before generic ones.
const HEURISTICS: { match: (h: string) => boolean; field: string }[] = [
  { match: h => /^(task\s*name|title|name|subject|summary)$/i.test(h), field: 'dagrun_pm_name' },
  { match: h => /^percent|%\s*complete|completion\s*%/i.test(h), field: 'dagrun_pm_percentcomplete' },
  { match: h => /(estimated|planned)\s*hours|estimate|hours$|work$/i.test(h), field: 'dagrun_pm_estimatedhours' },
  { match: h => /(due\s*date|due|deadline|finish)/i.test(h), field: 'dagrun_pm_duedate' },
  { match: h => /(start\s*date|start)/i.test(h), field: 'dagrun_pm_startdate' },
  { match: h => /(assigned\s*to|assignee|owner|responsible|resource|person)/i.test(h), field: 'dagrun_pm_assigneename' },
  { match: h => /status/i.test(h), field: 'dagrun_pm_status' },
  { match: h => /priority/i.test(h), field: 'dagrun_pm_priority' },
  { match: h => /description|note|detail/i.test(h), field: 'dagrun_pm_description' },
];

function autoMap(header: string): string {
  const h = header.trim();
  for (const rule of HEURISTICS) {
    if (rule.match(h)) return rule.field;
  }
  return '(skip)';
}

export default function MappingStep({ source, connectionData, onContinue }: Props) {
  const isPlanner = source === ImportSourceType.Planner;
  const headers: string[] = connectionData?.headers ?? [];

  const [mapping, setMapping] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    headers.forEach(h => { m[h] = autoMap(h); });
    return m;
  });

  if (isPlanner) {
    return (
      <div>
        <h3>Field Mapping — Planner</h3>
        <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: 8, textAlign: 'left' }}>Planner Field</th><th style={{ textAlign: 'left' }}>Dagrun PM Field</th>
          </tr></thead>
          <tbody>
            <tr><td style={{ padding: 6 }}>title</td><td>dagrun_pm_name</td></tr>
            <tr><td style={{ padding: 6 }}>percentComplete</td><td>dagrun_pm_percentcomplete</td></tr>
            <tr><td style={{ padding: 6 }}>dueDateTime</td><td>dagrun_pm_duedate</td></tr>
            <tr><td style={{ padding: 6 }}>assignments</td><td>dagrun_pm_assigneename</td></tr>
            <tr><td style={{ padding: 6 }}>bucketId</td><td>dagrun_pm_wipcollectionid (collection)</td></tr>
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: 'var(--ink-4)' }}>Planner mapping is fixed and not editable.</p>
        <button className="btn-primary" onClick={() => onContinue(mapping)} style={{ marginTop: 16 }}>Continue</button>
      </div>
    );
  }

  if (headers.length === 0) {
    return (
      <div>
        <h3>Field Mapping</h3>
        <p style={{ color: 'var(--ink-4)' }}>Connect to a source with mappable columns first.</p>
      </div>
    );
  }

  const mappedTo = (f: string) => Object.values(mapping).filter(x => x === f).length;
  const nameMapped = mappedTo('dagrun_pm_name') === 1;

  return (
    <div>
      <h3>Field Mapping{connectionData?.kind === 'rowset' ? '' : ''}</h3>
      <p style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 10 }}>
        Columns are pre-mapped using title heuristics. Override any row by selecting a different field or <code>(skip)</code>.
      </p>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
        <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: 8, textAlign: 'left' }}>Source Column</th>
          <th style={{ textAlign: 'left' }}>Dagrun PM Field</th>
        </tr></thead>
        <tbody>
          {headers.map(h => (
            <tr key={h} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
              <td style={{ padding: 6 }}>{h}</td>
              <td>
                <select value={mapping[h] ?? '(skip)'} onChange={e => setMapping({ ...mapping, [h]: e.target.value })}>
                  {DAGRUN_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!nameMapped && (
        <div style={{ padding: 10, marginTop: 10, background: '#FEF3C7', borderLeft: '3px solid #D97706', color: '#92400E', fontSize: 12, borderRadius: 4 }}>
          Map at least one column to <code>dagrun_pm_name</code> — tasks without a name will use a placeholder.
        </div>
      )}
      <button className="btn-primary" onClick={() => onContinue(mapping)} style={{ marginTop: 16 }}>Continue</button>
    </div>
  );
}
